import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isTerminalResolution } from '@/shared/hooks';
import { DeepLinkInterstitial } from '@/shared/ui';
import { BreadcrumbPageHeader, NavigationPanel } from '@/shared/ui/layout';
import { ROUTES } from '@/shared/config/routes';
import { FolderFormSheet } from '@/features/guidelines/ui/FolderFormSheet';
import { FolderGuidelinesPane } from '@/features/guidelines/ui/FolderGuidelinesPane';
import type { FolderDto } from '@/lib/api/guidelines-client';
import { GuidelinesPaneLoading } from './GuidelinesPaneLoading';
import { useGuidelinesWorkspace } from './hooks/useGuidelinesWorkspace';
import type { GuidelinesWorkspaceProps } from './GuidelinesWorkspace.types';

interface FormSheetState {
  open: boolean;
  mode: 'create' | 'edit';
  folder: FolderDto | null;
}

const CLOSED_FORM_SHEET: FormSheetState = { open: false, mode: 'create', folder: null };

/**
 * Shell for the folders `NavigationPanel` and the pane of the folder it opens.
 * The rail's state lives here, above the pane, which is remounted by `key` so
 * its own search/sort state resets per folder switch instead of needing a
 * reset effect. `FolderFormSheet` is mounted once here rather than inside
 * the rail or the pane, since both need to open it (create from the rail,
 * edit from the pane's identity block).
 */
export function GuidelinesWorkspace({
  resolution = { status: 'ready' },
}: GuidelinesWorkspaceProps) {
  const { t } = useTranslation('knowledge');

  const { folders, isLoadingFolders, activeFolderId, canCreate, hrefFor } =
    useGuidelinesWorkspace();

  const [formSheet, setFormSheet] = useState<FormSheetState>(CLOSED_FORM_SHEET);

  const openCreate = () => setFormSheet({ open: true, mode: 'create', folder: null });
  const closeFormSheet = () => setFormSheet(CLOSED_FORM_SHEET);
  const openEditActiveFolder = () => {
    const activeFolder = folders.find((folder) => folder.id === activeFolderId) ?? null;
    setFormSheet({ open: true, mode: 'edit', folder: activeFolder });
  };

  const items = folders.map((folder) => ({ id: folder.id, label: folder.title }));
  const createLabel = t('guidelines.foldersToolbar.createGuidelineFolder');
  const createTooltip = canCreate
    ? createLabel
    : t('guidelines.foldersToolbar.youDonTHavePermission');

  const paneContent =
    resolution.status === 'resolving' ? (
      <GuidelinesPaneLoading />
    ) : isTerminalResolution(resolution) ? (
      <DeepLinkInterstitial state={resolution} entity="guidelinesFolder" />
    ) : (
      <FolderGuidelinesPane
        key={activeFolderId ?? 'none'}
        folderId={activeFolderId}
        onEditFolder={openEditActiveFolder}
      />
    );

  return (
    <div className="flex h-full min-h-0 flex-col gap-xs">
      <BreadcrumbPageHeader
        trail={[{ label: t('guidelines.guidelinesHeader.knowledge'), href: ROUTES.KNOWLEDGE_HUB }]}
        page={t('guidelines.guidelinesHeader.brandVisualGuidelines')}
      />
      <div className="flex min-h-0 flex-1 gap-xs">
        <NavigationPanel
          data-testid="guideline-folders-panel"
          title={t('guidelines.foldersToolbar.guidelinesFolders')}
          items={items}
          activeItemId={activeFolderId}
          isLoading={isLoadingFolders}
          emptyMessage={t('guidelines.guidelineFoldersPanel.noFoldersYet')}
          createLabel={createLabel}
          createTooltip={createTooltip}
          canCreate={canCreate}
          onCreate={openCreate}
          hrefFor={hrefFor}
        />
        {paneContent}
      </div>
      <FolderFormSheet
        // Remounts on every open so its internal `useState` fields re-read
        // `folder` at mount time instead of keeping whatever the previous
        // open session left behind — the same mechanism the legacy page
        // used (`key={isEditModalOpen ? ... : 'edit-folder-closed'}`).
        key={formSheet.open ? `${formSheet.mode}-${formSheet.folder?.id ?? 'none'}` : 'closed'}
        open={formSheet.open}
        mode={formSheet.mode}
        folder={formSheet.folder}
        onClose={closeFormSheet}
      />
    </div>
  );
}

GuidelinesWorkspace.displayName = 'GuidelinesWorkspace';
